import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AlertCircle, ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui';
import {
  useWithdrawalCreate,
  useWithdrawalGetBalance,
  getWithdrawalGetBalanceQueryKey,
  getWithdrawalListQueryKey,
} from '@/lib/endpoints';
import { dollarsToCents, formatCurrency, sanitizeAmountInput } from '@/lib/format-currency';
import { useAmountInput } from '@/hooks/useAmountInput';
import { parsePaymentApiError } from '@/lib/payments/parseApiError';
import { OtpCancelledError } from '@/lib/otp-errors';
import { useCapabilityEnabled, KILL_SWITCH } from '@/lib/launchdarkly/killSwitches';
import { useOpenKycGate } from '@/store/kycGateStore';
import { useKycIdentityPending, IDENTITY_FLOW_KEY } from '@/lib/kyc/useKycIdentityPending';
import { PayComProvider } from '@/lib/paycom/PayComProvider';
import type { SavedBankInstrument, SavedCardInstrument } from '@/lib/paycom/types';
import type { PaymentSessionResponseDto } from '@/lib/models/paymentSessionResponseDto';
import type { WithdrawalResponseDto } from '@/lib/models/withdrawalResponseDto';
import { AXIOS_INSTANCE } from '@/lib/use-custom-instance';
import { authClient } from '@/auth';
import { cn } from '@/lib/utils';
import { WithdrawMethodPills, type WithdrawMethod } from './WithdrawMethodPills';
import { CardWithdrawPanel } from './CardWithdrawPanel';
import { BankWithdrawPanel } from './BankWithdrawPanel';
import { SkrillWithdrawPanel } from './SkrillWithdrawPanel';
import { PendingWithdrawalsList } from './PendingWithdrawalsList';
import { WithdrawFormSkeleton } from './WithdrawFormSkeleton';
import { WithdrawSuccess } from './WithdrawSuccess';
import { WithdrawFail } from './WithdrawFail';
import { WithdrawStepper, type WithdrawStep } from './WithdrawStepper';
import { WithdrawVerification } from './WithdrawVerification';
import { useOtpStatus, useOtpStore } from '@/store/otpStore';

const MIN_CENTS = 100;

interface SkrillSelection {
  email: string;
  isValid: boolean;
}

// One PayComProvider for the whole withdraw flow. The session is minted
// with the active method's `paymentMethodType` so the SDK iframe runs the
// right surface — card field for cards, Plaid Link for bank accounts.
// Swapping card↔bank re-mints via `sessionKey`.
//
// Minting is deferred: `POST /withdrawals/setup-session` fires only once the
// user commits an amount and picks a card/bank method (tapping a method pill
// is the amount screen's CTA) — not on page load. An empty `sessionKey` keeps
// the provider idle until then. Skrill never drives the SDK, so it never
// mints; once minted for card/bank, the provider stays mounted so users can
// flip between methods without a refresh.
export function WithdrawForm() {
  const [method, setMethod] = useState<WithdrawMethod>('card');
  const [sdkNeeded, setSdkNeeded] = useState(false);

  // Mint the session against the SDK surface the active method needs.
  const sdkMethod: 'card' | 'us_bank_account' = method === 'bank' ? 'us_bank_account' : 'card';
  const sessionFetcher = useCallback(() => createSetupSession(sdkMethod), [sdkMethod]);
  const startSdkSession = useCallback(() => setSdkNeeded(true), []);

  return (
    <PayComProvider sessionFetcher={sessionFetcher} sessionKey={sdkNeeded ? `withdraw-setup-${sdkMethod}` : ''}>
      <WithdrawFormInner method={method} setMethod={setMethod} onSdkNeeded={startSdkSession} />
    </PayComProvider>
  );
}

// TODO: migrate to generated hook once api annotates @ApiOkResponse on
// `POST /withdrawals/setup-session`. The Orval-generated
// `useWithdrawalCreateSetupSessionHook` takes only an AbortSignal today,
// so a body passed through it would land in the signal slot. Drop this
// helper once regen lands.
//
// `returnUrl` is always set to the current page so the Pay.com SDK page
// can navigate the player back after tokenization. The api echoes it
// verbatim in the response (it is *not* forwarded to Pay.com — its
// `/v1/sessions/setup` endpoint 400s on `return_url`).
async function createSetupSession(paymentMethodType: 'card' | 'us_bank_account'): Promise<PaymentSessionResponseDto> {
  const response = await AXIOS_INSTANCE.post<PaymentSessionResponseDto>(
    '/withdrawals/setup-session',
    { paymentMethodType, returnUrl: window.location.href },
    { headers: authClient.getAuthHeaders() },
  );
  return response.data;
}

interface InnerProps {
  method: WithdrawMethod;
  setMethod: (next: WithdrawMethod) => void;
  /** Ask the owner to mint the Pay.com setup session — called on the first
   *  card/bank method commit, which is the earliest the SDK is needed. */
  onSdkNeeded: () => void;
}

/**
 * The withdraw flow is a step wizard (ARC-150): the amount screen (balance +
 * amount + method buttons) hands off to a per-method confirm screen (stepper
 * "Choose Method"), which submits into the OTP challenge ("Verification") and
 * lands on a full-page result ("Withdraw Complete" / fail). The amount is
 * committed on screen one — tapping a method IS the call to action.
 */
function WithdrawFormInner({ method, setMethod, onSdkNeeded }: InnerProps) {
  const queryClient = useQueryClient();
  const balanceQuery = useWithdrawalGetBalance();

  // The OTP slot is registered at the WalletPage level (so the global
  // `<OtpModal>` stays suppressed across the Deposit/Withdraw tab switch). Here
  // we only read the OTP state to drive the inline verification step + stepper.
  const otpStatus = useOtpStatus();
  const cancelOtp = useOtpStore((s) => s.cancelOtp);
  const withdrawalsEnabled = useCapabilityEnabled(KILL_SWITCH.WITHDRAWAL_REQUEST);
  const openKycGate = useOpenKycGate();
  const kycPending = useKycIdentityPending();

  // 'amount' (enter amount, pick a method) or 'method' (confirm screen).
  // The OTP challenge and the result pages take precedence over both.
  const [screen, setScreen] = useState<'amount' | 'method'>('amount');

  // ── Result pages ─────────────────────────────────────────────────────
  // A successful request shows the full-page "Withdraw Complete" step; a
  // submit-level failure shows the full-page fail state (stepper parked at
  // "Verification", where the request died).
  const [successResult, setSuccessResult] = useState<WithdrawalResponseDto | null>(null);
  const [successOpen, setSuccessOpen] = useState(false);
  const [failOpen, setFailOpen] = useState(false);

  // Latest selection from each panel. Stored at this level only so the
  // submit handler can dispatch the right payload — panels own the
  // input UI state themselves.
  const [cardSelection, setCardSelection] = useState<SavedCardInstrument | null>(null);
  const [bankSelection, setBankSelection] = useState<SavedBankInstrument | null>(null);
  const [skrillSelection, setSkrillSelection] = useState<SkrillSelection>({ email: '', isValid: false });

  // Per-method setup overlay flags. When set, the form area is replaced
  // by the corresponding setup component (Pay.com card field or Plaid Link).
  const [addingCard, setAddingCard] = useState(false);
  const [addingBank, setAddingBank] = useState(false);

  const balanceCents = balanceQuery.data ? dollarsToCents(balanceQuery.data.balance) : 0;
  const {
    amountInput,
    amountCents,
    touched,
    serverError,
    setAmountInput,
    markTouched,
    clearTouched,
    setServerError,
    reset: resetAmount,
    clientError,
  } = useAmountInput({
    minCents: MIN_CENTS,
    balanceCents,
    messages: {
      belowMin: (cents) => `Minimum withdraw is ${formatCurrency(cents)}.`,
    },
  });

  const mutation = useWithdrawalCreate({
    mutation: {
      onSuccess: (data) => {
        // Capture the response before resetting so the success page can show
        // the requested amount, reference and status.
        setSuccessResult(data);
        setSuccessOpen(true);
        resetAmount();
        queryClient.invalidateQueries({ queryKey: getWithdrawalGetBalanceQueryKey() });
        queryClient.invalidateQueries({ queryKey: getWithdrawalListQueryKey() });
      },
      onError: (err: unknown) => {
        // Backing out of the OTP challenge is a user choice, not a failure —
        // fall back to the method screen quietly.
        if (err instanceof OtpCancelledError) return;
        // Keep the inline message for context under the amount field, and show
        // the full-page fail state for the submit-level failure.
        setServerError(parsePaymentApiError(err));
        setFailOpen(true);
      },
    },
  });

  const handleCardSelection = useCallback((card: SavedCardInstrument | null) => {
    setCardSelection(card);
  }, []);
  const handleBankSelection = useCallback((account: SavedBankInstrument | null) => {
    setBankSelection(account);
  }, []);
  const handleSkrillSelection = useCallback((value: SkrillSelection) => {
    setSkrillSelection(value);
  }, []);

  if (balanceQuery.isLoading) {
    return <WithdrawFormSkeleton />;
  }
  if (balanceQuery.isError) {
    return (
      <div role="alert" className="flex items-start gap-3 rounded-card border border-danger-alt/30 bg-danger-alt/[0.04] p-4">
        <AlertCircle className="size-5 text-danger-alt shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-primary">Couldn't load your wallet</p>
          <p className="text-xs text-muted mt-1">Refresh the page to try again.</p>
        </div>
      </div>
    );
  }

  // Setup overlays replace the entire form view. When the user finishes
  // tokenizing a card / linking a bank, the panel flips the flag off and
  // the inline picker re-renders with the new instrument selected.
  if (addingCard) {
    return (
      <CardWithdrawPanel
        mode="setup"
        onSelectionChange={handleCardSelection}
        onRequestAddNew={() => setAddingCard(true)}
        onCancelAddNew={() => {
          setAddingCard(false);
          setMethod('card');
        }}
      />
    );
  }

  if (addingBank) {
    return (
      <BankWithdrawPanel
        mode="setup"
        onSelectionChange={handleBankSelection}
        onRequestAddNew={() => setAddingBank(true)}
        onCancelAddNew={() => {
          setAddingBank(false);
          setMethod('bank');
        }}
      />
    );
  }

  const error = serverError || clientError;
  const amountValid = amountCents != null && !clientError;
  const canConfirm =
    amountValid &&
    !mutation.isPending &&
    withdrawalsEnabled &&
    (method === 'card' ? !!cardSelection : method === 'bank' ? !!bankSelection : skrillSelection.isValid);

  function handleMethodSelect(next: WithdrawMethod) {
    if (!amountValid) return;
    setMethod(next);
    // The amount + method are now committed — this is the earliest point the
    // Pay.com SDK can be needed, so mint the setup session here (not on page
    // load). Skrill never touches the SDK, so it doesn't mint one.
    if (next !== 'skrill') onSdkNeeded();
    // Entering a method screen discards the touched flag so unrelated
    // method-side client errors don't carry over. Amount is preserved.
    clearTouched();
    setScreen('method');
  }

  function backToAmount() {
    setScreen('amount');
  }

  function clearServerErrorOnInput() {
    if (serverError) setServerError('');
  }

  function dismissSuccess() {
    // "Got It" — back to a fresh amount screen (the amount was already reset
    // when the request succeeded).
    setSuccessOpen(false);
    setScreen('amount');
  }

  function dismissFail() {
    // Back to the method screen with state intact so the user can retry.
    setFailOpen(false);
  }

  function handleValidate() {
    setFailOpen(false);
    // Route the user to the identity gate so they can re-validate their
    // details. Use the pending flow when one is known, else the stable
    // identity flow key.
    void openKycGate(kycPending?.flowKey ?? IDENTITY_FLOW_KEY);
  }

  function handleSubmit(e: React.SyntheticEvent<HTMLFormElement>) {
    e.preventDefault();
    // Enter on the amount screen must not submit — the methods are the CTA.
    if (screen !== 'method') return;
    markTouched();
    setServerError('');
    // UX guard — the server also enforces this with a 503 FEATURE_DISABLED.
    if (!withdrawalsEnabled) return;
    if (amountCents == null || clientError) return;
    if (method === 'card') {
      if (!cardSelection) return;
      mutation.mutate({
        data: {
          amount: amountCents,
          paymentMethodToken: cardSelection.id,
          paymentMethodBrand: cardSelection.brand,
          paymentMethodLast4: cardSelection.last4,
        },
      });
      return;
    }
    if (method === 'bank') {
      if (!bankSelection) return;
      // Cast: same regen-pending reasoning as the setup-session call —
      // axios drops unknown fields server-side until `yarn generate-api`
      // refreshes the dto. Once regen lands, drop the cast.
      mutation.mutate({
        data: {
          amount: amountCents,
          usBankAccountToken: bankSelection.id,
          bankAccountLast4: bankSelection.last4,
          bankName: bankSelection.bankName,
          bankAccountType: bankSelection.accountType,
          ...(bankSelection.accountHolderName ? { bankAccountHolderName: bankSelection.accountHolderName } : {}),
        } as never,
      });
      return;
    }
    mutation.mutate({
      data: {
        amount: amountCents,
        skrillEmail: skrillSelection.email,
      },
    });
  }

  const hasAmount = amountInput.length > 0;
  const hasFunds = balanceCents >= MIN_CENTS;

  // ── Flow pages (Choose Method → Verification → Withdraw Complete) ──────
  // Any state past the amount screen renders under the back link + stepper.
  const otpOpen = otpStatus === 'open';
  const showSuccess = successOpen && !!successResult;
  const inFlow = screen === 'method' || otpOpen || showSuccess || failOpen;

  if (inFlow) {
    const stepperCurrent: WithdrawStep = showSuccess ? 'complete' : otpOpen || failOpen ? 'verification' : 'choose-method';
    const handleBack = showSuccess ? dismissSuccess : failOpen ? dismissFail : otpOpen ? cancelOtp : backToAmount;

    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={handleBack}
            className="-ml-1 flex items-center gap-1 self-start text-purple transition-opacity hover:opacity-70"
          >
            <ChevronLeft className="size-5" strokeWidth={2.5} />
            <span className="text-[14px] font-semibold capitalize">Withdraw</span>
          </button>
          <WithdrawStepper current={stepperCurrent} />
        </div>

        {showSuccess ? (
          <WithdrawSuccess result={successResult} onDismiss={dismissSuccess} />
        ) : failOpen ? (
          <WithdrawFail onValidate={handleValidate} />
        ) : otpOpen ? (
          <div className="flex flex-col gap-4">
            <h2 className="text-[18px] font-semibold text-ink">Verification</h2>
            <WithdrawVerification />
          </div>
        ) : (
          <>
            {/* ── Method confirm screen ─────────────────────────── */}
            <div className="flex flex-col gap-1">
              <span className="text-[18px] font-semibold text-ink">Withdrawal Amount</span>
              <span className="text-[45px] font-semibold leading-tight tracking-tight text-purple tabular-nums">
                {amountCents != null ? formatCurrency(amountCents) : ''}
              </span>
            </div>

            {/*
              All three panels stay mounted so per-method selection state
              (saved card, bank account, Skrill email) survives leaving and
              re-entering method screens. Inactive panels are hidden via
              `hidden`; React keeps their `useState` intact.
            */}
            <div hidden={method !== 'card'}>
              <CardWithdrawPanel
                mode="pick"
                onSelectionChange={handleCardSelection}
                onRequestAddNew={() => setAddingCard(true)}
                onCancelAddNew={() => setAddingCard(false)}
              />
            </div>
            <div hidden={method !== 'bank'}>
              <BankWithdrawPanel
                mode="pick"
                onSelectionChange={handleBankSelection}
                onRequestAddNew={() => setAddingBank(true)}
                onCancelAddNew={() => setAddingBank(false)}
              />
            </div>
            <div hidden={method !== 'skrill'}>
              <SkrillWithdrawPanel
                onSelectionChange={handleSkrillSelection}
                touched={touched}
                onUserInput={clearServerErrorOnInput}
              />
            </div>

            <Button type="submit" size="lg" disabled={!canConfirm}>
              {!withdrawalsEnabled ? 'Withdrawals temporarily paused' : mutation.isPending ? 'Submitting…' : 'Confirm'}
            </Button>
          </>
        )}
      </form>
    );
  }

  // ── Amount screen ─────────────────────────────────────────────────────
  // No balance hero here: the wallet page's shared gradient hero (the same
  // one the Portfolio uses) sits directly above the tab strip.
  return (
    <div className="flex flex-col gap-6">
      <PendingWithdrawalsList />

      <div className="flex flex-col gap-6">
        {/* ── Amount ───────────────────────────────────────────── */}
        <div className="flex flex-col gap-2">
          <label htmlFor="withdraw-amount" className="px-1 text-[18px] font-semibold text-ink">
            Enter Amount
          </label>
          <div
            className={cn(
              'flex h-12 items-center gap-2 rounded-pill border-2 bg-surface px-5 transition-colors',
              error ? 'border-danger-alt/50' : 'border-stroke-soft focus-within:border-purple/40',
            )}
          >
            <span className={cn('text-[20px] font-medium', hasAmount ? 'text-ink' : 'text-ink-muted/60')}>$</span>
            <input
              id="withdraw-amount"
              inputMode="decimal"
              placeholder="0"
              value={amountInput}
              onChange={(e) => setAmountInput(sanitizeAmountInput(e.target.value))}
              autoComplete="off"
              aria-invalid={!!error}
              aria-label="Amount"
              disabled={!hasFunds}
              className="w-full border-0 bg-transparent text-[18px] font-medium text-ink caret-purple outline-0 placeholder:text-ink-muted/40 focus-visible:outline-none disabled:opacity-60"
            />
          </div>
          {error ? (
            <span
              role="alert"
              className="inline-flex items-center gap-1.5 self-start rounded-pill bg-danger-alt/10 px-3 py-1 text-xs font-medium text-danger-alt"
            >
              {error}
            </span>
          ) : !hasFunds ? (
            <span className="px-1 text-xs font-medium text-ink-muted">Add funds before requesting a withdrawal.</span>
          ) : null}
        </div>

        {/* ── Methods (the CTA: tapping one advances the flow) ───── */}
        <div className="flex flex-col gap-2">
          <span className="px-1 text-[18px] font-semibold text-ink">Withdrawal Methods</span>
          <WithdrawMethodPills onSelect={handleMethodSelect} disabled={!amountValid || !withdrawalsEnabled} />
          {!withdrawalsEnabled && (
            <span className="px-1 text-xs font-medium text-ink-muted">Withdrawals temporarily paused.</span>
          )}
        </div>
      </div>
    </div>
  );
}
